import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { ResetPlateMode } from 'enums/machine.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { ITrainingMsg } from 'lib_ts/interfaces/machine-msg/i-training';
import { useContext, useEffect } from 'react';
import { TrainedMsService } from 'services/trained-machine-states.service';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const MAX_CONTINUOUS_TRAINING_SHOTS = 50;

const useContTrainingShotListener = () => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const { machine } = useContext(MachineContext);

  const { pitchBP, lastBsHash, lastSendConfig, setAndSendForBP } =
    useContext(AimingContext);
  const latestPitchBP = useLatest(pitchBP);
  const latestBsHash = useLatest(lastBsHash);
  const latestSendConfig = useLatest(lastSendConfig);
  const latestSend = useLatest(setAndSendForBP);

  const readyToContTrain = useTrainedMsStore((s) => s.readyToContTrain);

  // continuous training w/ shots arriving live
  useEffect(() => {
    if (onDemand) {
      // during on-demand, do not interact with TMS
      return;
    }

    if (!readyToContTrain) {
      // * disabling CT will also pause accumulation of contShotErrors
      // e.g. machine's tracking device isn't supported OR continuous training is disabled
      console.debug('useContTrainingShotListener: not ready to cont train');
      return;
    }

    const handleNewShot = async (event: CustomEvent) => {
      const data = event.detail as ITrainingMsg;

      console.debug('useContTrainingShotListener: handling new shot', data);

      if (!data.success) {
        // ignore interim training messages w/ undefined (e.g. when individual tracking devices report their own detections)
        // ignore failure training messages w/ false
        return;
      }

      if (!data.shot) {
        // can't do anything if a shot was not created
        return;
      }

      if (data.shot.training) {
        // skip any shots from training b/c CT should only be applied to BP shots
        return;
      }

      if (data.shot.machineID !== machine.machineID) {
        // shouldn't trigger but just in case
        return;
      }

      if (!data.shot.bs_hash) {
        // bs_hash comes from shot to ensure the correct TMS is used even if the UI has moved onto another pitch
        return;
      }

      const nextError = TrainingHelper.getContShotError(data.shot);

      if (!nextError) {
        // shot failed to generate an error (e.g. failed confidence checks)
        console.debug(
          'useContTrainingShotListener: shot failed to generate an error',
          data
        );
        return;
      }

      // get the current tms value to append the shot ID to
      const tms = useTrainedMsStore.getState().getTms(data.shot.bs_hash);

      const nextErrors = tms?.contShotErrors ?? [];
      nextErrors.push(nextError);

      const upsertedTms = await useTrainedMsStore.getState().upsertTms({
        // for finding TMS
        machineID: machine.machineID,
        ball_type: machine.ball_type,
        bs_hash: data.shot.bs_hash,

        // updated
        contShotErrors: nextErrors.slice(-MAX_CONTINUOUS_TRAINING_SHOTS),
        deleted: false,
      });

      if (!upsertedTms) {
        // shouldn't trigger, but upsert failed
        return;
      }

      const shouldAdjust =
        TrainingHelper.shouldAdjustForContTraining(upsertedTms);

      if (!shouldAdjust) {
        return;
      }

      useTrainedMsStore.setState({ adjusting: true });

      const adjustedTms = await TrainedMsService.getInstance()
        .adjustForContTraining(upsertedTms._id)
        .finally(() => useTrainedMsStore.setState({ adjusting: false }));

      if (!adjustedTms) {
        // shouldn't trigger, but adjust failed
        return;
      }

      // update what's held in the store to whatever the server returned
      useTrainedMsStore
        .getState()
        .mergeTms({ [adjustedTms.bs_hash]: adjustedTms }, []);

      // check if the last send was related to this TMS record
      if (latestBsHash.current !== adjustedTms.bs_hash) {
        // adjusted TMS is not relevant to what's currently on the machine
        return;
      }

      if (latestPitchBP.current?.bs_hash !== adjustedTms.bs_hash) {
        // sanity check
        return;
      }

      if (!latestSendConfig.current) {
        // sanity check
        return;
      }

      // auto-send the new contMs
      latestSend.current({
        pitch: latestPitchBP.current,
        resetPlate: ResetPlateMode.Skip,
        sendConfig: {
          ...latestSendConfig.current,
          overrideTms: adjustedTms,
        },
      });
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, handleNewShot);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, handleNewShot);
    };
  }, [
    onDemand,
    readyToContTrain,
    machine.machineID,
    machine.ball_type,
    machine.tracking_device,
  ]);
};

export default useContTrainingShotListener;
